import { useCallback } from "react";
import { useSelectedLocation } from "../../../hooks";
import { createApiClient, apiClient } from "../../../api/apiClient";
import { DropdownItem } from "../../../types/observation";
import {
  handleSparcWalkErrorWithFriendlyMessage,
  SPARC_WALK_API_NAMES,
} from "../../../store/utils/sparcWalkErrorHandler";

interface UseZoneOptionsProps {
  setZoneOptions: (options: DropdownItem[]) => void;
}

/**
 * Custom hook to fetch zone options for SPARC Walk
 */
const useZoneOptions = ({ setZoneOptions }: UseZoneOptionsProps) => {
  const selectedLocation = useSelectedLocation();

  const fetchZoneOptions = useCallback(async () => {
    if (!selectedLocation) return;

    try {
      const location = encodeURIComponent(
        (selectedLocation as any)?.displayText || (selectedLocation as any)?.name || "Hayward CA"
      );
      const api = createApiClient();
      const data = await apiClient(
        api,
        `/sparc-walks/zones?location=${location}`,
        "GET"
      );
      
      if (!data || !Array.isArray(data)) {
        throw new Error("Invalid zone data received");
      }
      
      const formatted: DropdownItem[] = data.map(
        (zoneObj: { ZONE_ID: number; ZONE: string }) => ({
          id: zoneObj.ZONE,
          displayText: zoneObj.ZONE,
        })
      );
      setZoneOptions(formatted);
    } catch (err) {
      console.error("Error fetching zone options:", err);
      
      // Use centralized error handler with modal overlay
      handleSparcWalkErrorWithFriendlyMessage(
        SPARC_WALK_API_NAMES.FETCH_ZONES,
        err,
        true, // Show modal overlay
        {
          location: selectedLocation,
        }
      );
      
      setZoneOptions([]);
    }
  }, [selectedLocation, setZoneOptions]);

  return {
    fetchZoneOptions,
    selectedLocation,
  };
};

export default useZoneOptions;
